import React, { Ref } from 'react';
import { Scroll } from './types';
import { DataTableHeader, DataTableHeaderProps } from './DataTableHeader';
import { FloatingHeader, TranslateInner } from './styles';
import { Table } from './Table';
import { TableColGroup } from './TableColGroup';

interface FloatingTableHeaderProps<DataType extends object>
  extends Omit<DataTableHeaderProps<DataType>, 'stickyHeader' | 'stickyOffset'> {
  cols: Array<{ width?: number; minWidth?: number }>;
  scroll?: Scroll;
  visible: boolean;
  stickyOffset: number;
  containerWidth: number;
  tableWidth: number;
  headerHeight: number;
  columnWidths: number[];
  headerRef?: Ref<HTMLDivElement>;
  translateInnerRef?: Ref<HTMLDivElement>;
}

export const FloatingTableHeader = <DataType extends object>({
  cols,
  scroll,
  visible,
  stickyOffset,
  containerWidth,
  tableWidth,
  headerHeight,
  columnWidths,
  headerRef,
  translateInnerRef,
  ...headerProps
}: FloatingTableHeaderProps<DataType>) => {
  const syncedCols =
    columnWidths.length === cols.length
      ? cols.map((col, index) => ({
          ...col,
          width: columnWidths[index],
          minWidth: columnWidths[index],
        }))
      : cols;
  const resolvedTableWidth = tableWidth || containerWidth;
  const isVisible = visible && containerWidth > 0 && headerHeight > 0;

  return (
    <FloatingHeader
      ref={headerRef}
      className='pds-table-floating-header'
      aria-hidden='true'
      $visible={isVisible}
      $stickyOffset={stickyOffset}
      $containerWidth={containerWidth}
      $headerHeight={headerHeight}
    >
      <TranslateInner
        ref={translateInnerRef}
        className='pds-table-floating-header-inner'
        $tableWidth={resolvedTableWidth}
      >
        <Table
          scroll={scroll}
          resize={headerProps.resize}
          style={resolvedTableWidth > 0 ? { width: resolvedTableWidth } : undefined}
        >
          <TableColGroup cols={syncedCols} />
          <thead>
            <DataTableHeader {...headerProps} stickyHeader={false} stickyOffset={0} />
          </thead>
        </Table>
      </TranslateInner>
    </FloatingHeader>
  );
};
